import type { ReactNode } from 'react';
import { Box, Card, Divider, Stack, Typography } from '@mui/joy';
import {
  MapPin as MapPinIcon,
  Ruler as RulerIcon,
  House as HouseIcon,
  SealPercent as OfferIcon,
} from '@phosphor-icons/react';
import { formatMoney, moneyToNumber } from '../../../lib/directMailApi';
import type { PublicMailerCampaign, PublicMailerQuoteDetail } from '../../../types/directMail';
import type { BrandPalette } from './brand';

interface MailerQuoteHeroProps {
  campaign: PublicMailerCampaign;
  quote: PublicMailerQuoteDetail;
  palette: BrandPalette;
}

interface RoofFact {
  icon: ReactNode;
  label: string;
  value: string;
}

function hasValue(value: string | number | null | undefined): boolean {
  return value !== null && value !== undefined && value !== '';
}

/** The printed piece leads with a range; a single number reads as a firm bid. */
function priceHeadline(quote: PublicMailerQuoteDetail): string | null {
  if (!hasValue(quote.price_low)) return null;
  const low = moneyToNumber(quote.price_low);
  const high = moneyToNumber(quote.price_high);
  if (!hasValue(quote.price_high) || high <= low) {
    return formatMoney(low, { decimals: 0 });
  }
  return `${formatMoney(low, { decimals: 0 })} – ${formatMoney(high, { decimals: 0 })}`;
}

function financingTerms(campaign: PublicMailerCampaign): string {
  const months = campaign.financing_months ?? 0;
  const apr = moneyToNumber(campaign.financing_apr);
  const parts: string[] = [];
  if (months > 0) parts.push(`${months} months`);
  if (apr > 0) {
    parts.push(`${apr.toLocaleString('en-US', { maximumFractionDigits: 2 })}% APR`);
  }
  parts.push('with approved credit');
  return parts.join(' · ');
}

function roofFacts(quote: PublicMailerQuoteDetail): RoofFact[] {
  const facts: RoofFact[] = [];

  if (hasValue(quote.roof_area_sqft)) {
    facts.push({
      icon: <RulerIcon size={20} weight="duotone" />,
      label: 'Roof size',
      value: `${moneyToNumber(quote.roof_area_sqft).toLocaleString('en-US', {
        maximumFractionDigits: 0,
      })} sq ft`,
    });
  }

  if (hasValue(quote.squares)) {
    facts.push({
      icon: <HouseIcon size={20} weight="duotone" />,
      label: 'Squares',
      value: moneyToNumber(quote.squares).toLocaleString('en-US', { maximumFractionDigits: 1 }),
    });
  }

  if (quote.predominant_pitch) {
    facts.push({
      icon: <HouseIcon size={20} weight="duotone" />,
      label: 'Pitch',
      value: quote.predominant_pitch,
    });
  }

  return facts;
}

export function MailerQuoteHero({ campaign, quote, palette }: MailerQuoteHeroProps) {
  const price = priceHeadline(quote);
  const facts = roofFacts(quote);
  const showFinancing = campaign.show_financing && hasValue(quote.monthly_payment);

  return (
    <Card
      variant="outlined"
      sx={{ p: 0, overflow: 'hidden', gap: 0, borderColor: palette.border, boxShadow: 'md' }}
    >
      <Box
        sx={{
          bgcolor: palette.base,
          color: palette.onBase,
          px: { xs: 2.5, sm: 4 },
          py: { xs: 3, sm: 4 },
          textAlign: 'center',
        }}
      >
        <Typography
          level="body-xs"
          sx={{
            color: 'inherit',
            opacity: 0.85,
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
          }}
        >
          Your personalized roof estimate
        </Typography>
        <Typography
          level="h2"
          sx={{ color: 'inherit', fontWeight: 800, mt: 1, fontSize: { xs: '1.65rem', sm: '2rem' } }}
        >
          {campaign.headline ||
            (price ? 'We measured your roof — here is your price' : 'Your free roof estimate')}
        </Typography>
        {quote.address && (
          <Stack
            direction="row"
            spacing={0.75}
            justifyContent="center"
            alignItems="center"
            sx={{ mt: 1.5 }}
          >
            <MapPinIcon size={16} weight="fill" />
            <Typography level="body-sm" sx={{ color: 'inherit', opacity: 0.92 }}>
              {quote.address}
            </Typography>
          </Stack>
        )}
      </Box>

      <Box sx={{ px: { xs: 2.5, sm: 4 }, py: { xs: 3, sm: 3.5 }, textAlign: 'center' }}>
        {price ? (
          <>
            <Typography
              level="body-xs"
              textColor="text.tertiary"
              sx={{ fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}
            >
              Estimated price
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                color: palette.base,
                fontWeight: 800,
                lineHeight: 1.1,
                fontSize: 'clamp(2.25rem, 11vw, 3.25rem)',
              }}
            >
              {price}
            </Typography>
            <Typography level="body-sm" textColor="text.secondary" sx={{ mt: 1 }}>
              Based on a measurement of your actual roofline — not a guess from the street.
            </Typography>
          </>
        ) : (
          <Typography level="title-lg" sx={{ fontWeight: 700 }}>
            Request your free, no-obligation roof estimate.
          </Typography>
        )}

        {showFinancing && (
          <Box
            sx={{
              mt: 2.5,
              py: 1.5,
              px: 2,
              borderRadius: 'md',
              bgcolor: palette.tint,
              border: '1px solid',
              borderColor: palette.border,
            }}
          >
            <Typography level="title-md" sx={{ fontWeight: 700 }}>
              or about {formatMoney(quote.monthly_payment, { decimals: 0 })} / month
            </Typography>
            <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 0.25 }}>
              {financingTerms(campaign)}
            </Typography>
          </Box>
        )}

        {facts.length > 0 && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))',
                gap: 1.5,
              }}
            >
              {facts.map((fact) => (
                <Stack key={fact.label} alignItems="center" spacing={0.5}>
                  <Box sx={{ color: palette.base, display: 'flex' }}>{fact.icon}</Box>
                  <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                    {fact.value}
                  </Typography>
                  <Typography level="body-xs" textColor="text.tertiary">
                    {fact.label}
                  </Typography>
                </Stack>
              ))}
            </Box>
          </>
        )}
      </Box>

      {campaign.offer_text && (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
          sx={{
            bgcolor: palette.tint,
            borderTop: '1px solid',
            borderColor: palette.border,
            px: { xs: 2.5, sm: 4 },
            py: 2,
          }}
        >
          <Box sx={{ color: palette.base, display: 'flex', mt: '2px' }}>
            <OfferIcon size={22} weight="fill" />
          </Box>
          <Typography level="title-sm" sx={{ fontWeight: 600 }}>
            {campaign.offer_text}
          </Typography>
        </Stack>
      )}
    </Card>
  );
}
